import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { ENDPOINT } from '@siloreal:app:config/endpoint';
import { PATH } from '@siloreal:app:config/path';
import { useGlobalStore } from '@siloreal:app:store';
import { useGlobalStore as useOldGlobalStore } from '@old/global-store/use-store';
import type { AuthResponse } from '@siloreal:shared:api/service/auth';

type AuthSession = {
  accessToken?: string;
  refreshToken?: string;
  expiration: number;
};

const refreshClient = axios.create({
  baseURL: import.meta.env.VITE_IOF_API,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise: Promise<string> | null = null;

function getTokenExpiration(token: string): number {
  const decoded = jwtDecode(token);
  return (decoded.exp ?? 0) * 1000;
}

function mapPermissions(data: AuthResponse) {
  const organizationPermission = data.user.user_organizations_permissions.find(
    (permission) => permission.organization_id === data.user.organization_id
  );

  return {
    organization: {
      id: parseInt(data.user.organization_id, 10),
      read: Boolean(organizationPermission?.read_permission),
      write: Boolean(organizationPermission?.write_permission),
      full: Boolean(organizationPermission?.full_permission),
    },
    countryside: data.user.user_countrysides_permissions.map((permission) => ({
      id: parseInt(permission.countryside_id, 10),
      read: Boolean(permission.read_permission),
      write: Boolean(permission.write_permission),
      full: Boolean(permission.full_permission),
    })),
  };
}

export function getAuthSession(): AuthSession {
  const {
    auth_access_token: accessToken,
    auth_refresh_token: refreshToken,
    auth_expiration_token_date: expiration,
  } = useGlobalStore.getState();
  const legacy = useOldGlobalStore.getState();

  return {
    accessToken: legacy.accessToken ?? accessToken,
    refreshToken: legacy.refreshToken ?? refreshToken,
    expiration: legacy.accessToken ? legacy.expirationTokenDate : expiration,
  };
}

export function applyAuthSession(data: AuthResponse): void {
  const { token, refresh_token: refreshToken, user, awsTempCredentials, integrations } = data;
  const expiration = getTokenExpiration(token);
  const language = user.prefered_language_id === 1 ? 'es' : 'pt';
  const permissions = mapPermissions(data);

  useGlobalStore.getState().auth_set_user_credentials({
    auth_access_token: token,
    auth_refresh_token: refreshToken,
    auth_expiration_token_date: expiration,
  });

  const legacy = useOldGlobalStore.getState();
  legacy.setAccessToken(token);
  legacy.setRefreshToken(refreshToken);
  legacy.setExpirationTokenDate(expiration);
  legacy.setPermissions(permissions);
  legacy.setUserID(Number(user.id));
  legacy.setUserFullname(user.fullname);
  legacy.setEmail(user.email);
  legacy.setOrganizationId(Number(user.organization_id));
  legacy.setUserOrganizationId(Number(user.organization_id));
  legacy.setOrganizationName(user.organizations.name);
  legacy.setSelectedOrganizationName(user.organizations.name);
  legacy.setOrganizationTaxId(user.organizations.tax_id);
  legacy.setOrganizationLogo(user.organizations.logo_url);
  legacy.setCountryId(user.organizations.country_id);
  legacy.setLanguage(language);
  legacy.setWasTutorialShow(user.was_tutorial_shown);
  legacy.setWas_agrotoken_integration_popup_shown(user.was_agrotoken_integration_popup_shown);
  legacy.setWas_lasegunda_integration_popup_shown(user.was_la_segunda_popup_shown);
  legacy.setWas_rus_popup_shown(user.was_rus_popup_shown);
  legacy.setWas_galicia_popup_shown(user.was_galicia_popup_shown ?? false);
  legacy.setWas_warrant_popup_shown(user.was_warrant_popup_shown ?? false);
  legacy.setCorvusIntegrated(integrations.corvus.isIntegrated);
  legacy.setAwsTempCredentials(awsTempCredentials);

  localStorage.setItem('LANGUAGE_PREFERENCE', language);
}

export function clearAuthSession(): void {
  useGlobalStore.getState().auth_clear();
  useOldGlobalStore.getState().clearAuthData();
}

function redirectToLogin(): void {
  if (typeof window === 'undefined' || window.location.pathname === PATH.AUTH_SILOREAL_LOGIN) return;
  window.location.replace(PATH.AUTH_SILOREAL_LOGIN);
}

function expireAuthSession(): void {
  clearAuthSession();
  redirectToLogin();
}

async function requestTokenRefresh(): Promise<string> {
  const { accessToken, refreshToken } = getAuthSession();

  if (!refreshToken) {
    expireAuthSession();
    throw new Error('No refresh token available');
  }

  try {
    const { data } = await refreshClient.post<AuthResponse>(
      ENDPOINT.LOGIN,
      { refresh_token: refreshToken },
      {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      }
    );

    applyAuthSession(data);
    return data.token;
  } catch (error) {
    if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
      expireAuthSession();
    }

    throw error;
  }
}

export function refreshAuthSession(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = requestTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}
